// src/components/ContractFileViewer.jsx
import React from "react";
import PdfViewer from "./PdfViewer";

export default function ContractFileViewer({
  url,
  height = 480,
  title = "Contract",
  filename = "",
  mimeType = "",
  forcePdf = false,
}) {
  if (!url) {
    return (
      <div style={{ ...styles.placeholder, minHeight: height }}>
        <p style={{ margin: 0 }}>
          Preview is not available for this file.
        </p>
      </div>
    );
  }

  const u = String(url);
  const resolvedFilename = filename || getFilenameFromUrl(u);
  const isPdf =
    Boolean(forcePdf) ||
    isPdfSource({
      url: u,
      filename: resolvedFilename,
      mimeType,
    });
  const isOfficeDocument = isOfficeSource({
    url: u,
    filename: resolvedFilename,
    mimeType,
  });
  const isImage = isImageSource({
    url: u,
    filename: resolvedFilename,
    mimeType,
  });

  // PDF → AskLawly in-app PDF viewer
  if (isPdf) {
    return (
      <div style={{ ...styles.pdfWrap, height }}>
        <PdfViewer
          src={u}
          title={title}
          filename={resolvedFilename || "contract.pdf"}
          downloadUrl={u}
          fallbackUrl={u}
          initialZoom="fit-width"
          defaultSidebarOpen={false}
          allowDownload
          allowPrint
          allowFullscreen
          allowBrowserFallback
          showTitle={false}
          style={styles.pdfViewer}
        />
      </div>
    );
  }

  // Word, Excel, PowerPoint, and RTF → Microsoft Office Online
  if (isOfficeDocument) {
    const officeUrl =
      `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(u)}`;

    return (
      <div style={{ ...styles.frameWrap, height }}>
        <iframe
          title={`${title || "Contract"} (Microsoft Office)`}
          src={officeUrl}
          style={styles.iframe}
          referrerPolicy="no-referrer"
          allowFullScreen
        />
      </div>
    );
  }

  // Image → inline image
  if (isImage) {
    return (
      <div style={{ ...styles.imageWrap, minHeight: height }}>
        <img
          src={u}
          alt={title || resolvedFilename || "Contract preview"}
          style={{
            ...styles.image,
            maxHeight: height,
          }}
        />
      </div>
    );
  }

  // Generic fallback for TXT, CSV, HTML, and other browser-viewable files.
  return (
    <div style={{ ...styles.frameWrap, height }}>
      <iframe
        title={`${title || "Contract"} (viewer)`}
        src={u}
        style={styles.iframe}
        referrerPolicy="no-referrer"
        allowFullScreen
      />
    </div>
  );
}

const styles = {
  frameWrap: {
    width: "100%",
    minWidth: 0,
    flex: 1,
    borderRadius: 8,
    overflow: "hidden",
    border: "1px solid #d1d5db",
    background: "#fff",
  },
  pdfWrap: {
    width: "100%",
    minWidth: 0,
    flex: 1,
    overflow: "hidden",
    borderRadius: 8,
    background: "#242424",
  },
  pdfViewer: {
    width: "100%",
    height: "100%",
    minWidth: 0,
    minHeight: 0,
  },
  iframe: {
    display: "block",
    width: "100%",
    height: "100%",
    border: "none",
  },
  imageWrap: {
    width: "100%",
    minWidth: 0,
    flex: 1,
    borderRadius: 8,
    overflow: "auto",
    border: "1px solid #d1d5db",
    background: "#fff",
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "center",
    padding: 12,
  },
  image: {
    display: "block",
    maxWidth: "100%",
    borderRadius: 8,
    objectFit: "contain",
    background: "#fff",
  },
  placeholder: {
    width: "100%",
    minWidth: 0,
    flex: 1,
    borderRadius: 8,
    border: "1px dashed #cbd5f5",
    background: "#eef2ff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 16,
    fontSize: 13,
    color: "#4b5563",
    textAlign: "center",
  },
};

const OFFICE_EXTENSIONS = new Set([
  "doc",
  "docx",
  "rtf",
  "xls",
  "xlsx",
  "ppt",
  "pptx",
]);

const IMAGE_EXTENSIONS = new Set([
  "png",
  "jpg",
  "jpeg",
  "gif",
  "webp",
  "bmp",
  "svg",
]);

function normalizedMimeType(value = "") {
  return String(value || "")
    .split(";")[0]
    .trim()
    .toLowerCase();
}

function getFilenameFromUrl(value = "") {
  const raw = String(value || "").trim();
  if (!raw) return "";

  try {
    const parsed = new URL(raw, window.location.href);

    const queryFilename =
      parsed.searchParams.get("filename") ||
      parsed.searchParams.get("fileName") ||
      parsed.searchParams.get("name") ||
      parsed.searchParams.get("response-content-disposition") ||
      "";

    if (queryFilename) {
      const dispositionMatch = queryFilename.match(
        /filename\*?=(?:UTF-8''|")?([^";]+)/i,
      );
      const candidate = dispositionMatch?.[1] || queryFilename;
      return decodeURIComponent(candidate.replace(/^["']|["']$/g, ""));
    }

    const pathName = parsed.pathname.split("/").filter(Boolean).pop() || "";
    return decodeURIComponent(pathName);
  } catch {
    const withoutHash = raw.split("#")[0];
    const withoutQuery = withoutHash.split("?")[0];
    return decodeURIComponent(
      withoutQuery.split("/").filter(Boolean).pop() || "",
    );
  }
}

function getExtension(...values) {
  for (const value of values) {
    const text = String(value || "").trim();
    if (!text) continue;

    const clean = text.split("#")[0].split("?")[0];
    const match = clean.match(/\.([a-z0-9]+)$/i);
    if (match?.[1]) return match[1].toLowerCase();
  }

  return "";
}

function isPdfSource({ url = "", filename = "", mimeType = "" } = {}) {
  const mime = normalizedMimeType(mimeType);
  const extension = getExtension(filename, getFilenameFromUrl(url), url);
  const source = `${url} ${filename} ${mime}`;

  return (
    extension === "pdf" ||
    mime === "application/pdf" ||
    /application\/pdf/i.test(source) ||
    /(?:contenttype|contentType|response-content-type|mime|mimeType|type)=application%2Fpdf/i.test(
      source,
    )
  );
}

function isOfficeSource({ url = "", filename = "", mimeType = "" } = {}) {
  const mime = normalizedMimeType(mimeType);
  const extension = getExtension(filename, getFilenameFromUrl(url), url);

  return (
    OFFICE_EXTENSIONS.has(extension) ||
    mime === "application/msword" ||
    mime === "application/rtf" ||
    mime === "text/rtf" ||
    mime ===
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
    mime === "application/vnd.ms-excel" ||
    mime ===
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
    mime === "application/vnd.ms-powerpoint" ||
    mime ===
      "application/vnd.openxmlformats-officedocument.presentationml.presentation"
  );
}

function isImageSource({ url = "", filename = "", mimeType = "" } = {}) {
  const mime = normalizedMimeType(mimeType);
  const extension = getExtension(filename, getFilenameFromUrl(url), url);

  return IMAGE_EXTENSIONS.has(extension) || mime.startsWith("image/");
}
